import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Facebook, Mail, MapPin, Phone } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { productCategories } from '../data/productCategories';
import { useTranslation } from 'react-i18next';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Custom TikTok icon component
const TikTokIcon = ({ className, style }) => (
  <svg className={className} style={style} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const Footer = () => {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const [companySettings, setCompanySettings] = useState({
    company_name: 'Okinawa Signs',
    phone: '',
    email: '',
    address: '',
    whatsapp: ''
  });

  // Fetch company settings on mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await fetch(`${API_URL}/api/company-settings`);
        if (response.ok) {
          const data = await response.json();
          setCompanySettings(prev => ({ ...prev, ...data }));
        }
      } catch (error) {
        console.error('Error fetching company settings:', error);
      }
    };
    fetchSettings();
  }, []);
  
  return (
    <footer 
      className="border-t py-12 px-4 transition-colors duration-300"
      style={{
        backgroundColor: isDark ? '#000000' : '#ffffff',
        borderColor: isDark ? '#ff00ff' : '#ff1493',
        borderTopWidth: '2px',
        boxShadow: isDark 
          ? '0 -10px 40px rgba(255, 0, 255, 0.15)' 
          : '0 -10px 30px rgba(255, 20, 147, 0.1)'
      }}
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
          {/* Brand */}
          <div className="col-span-1 md:col-span-1">
            <h3 
              className={`text-2xl font-bold mb-4 ${isDark ? 'gradient-text-dark' : 'gradient-text-light'}`}
              style={{
                filter: isDark 
                  ? 'drop-shadow(0 0 15px rgba(255, 0, 255, 0.5))'
                  : 'drop-shadow(0 0 12px rgba(255, 20, 147, 0.4))'
              }}
            >
              Okinawa Signs
            </h3>
            <p 
              className="mb-4 max-w-md transition-colors duration-300 text-sm"
              style={{ color: isDark ? '#888' : '#64748b' }}
            >
              {t('footer.description')}
            </p>
            <div className="flex gap-4">
              <a 
                href="https://www.instagram.com/okinawasignstudio/" 
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110"
                style={{
                  backgroundColor: isDark ? '#0a0a0a' : '#f8fafc',
                  borderColor: isDark ? '#ff00ff' : '#ff1493',
                  borderWidth: '2px',
                  borderStyle: 'solid',
                  boxShadow: isDark ? '0 0 15px rgba(255, 0, 255, 0.3)' : '0 0 12px rgba(255, 20, 147, 0.3)'
                }}
              >
                <Instagram className="w-5 h-5" style={{ color: isDark ? '#ff00ff' : '#ff1493' }} />
              </a>
              <a 
                href="#" 
                className="w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110"
                style={{
                  backgroundColor: isDark ? '#0a0a0a' : '#f8fafc',
                  borderColor: isDark ? '#00ffff' : '#0ea5e9',
                  borderWidth: '2px',
                  borderStyle: 'solid',
                  boxShadow: isDark ? '0 0 15px rgba(0, 255, 255, 0.3)' : '0 0 12px rgba(14, 165, 233, 0.3)'
                }}
              >
                <Facebook className="w-5 h-5" style={{ color: isDark ? '#00ffff' : '#0ea5e9' }} />
              </a>
              <a 
                href="#" 
                className="w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110"
                style={{
                  backgroundColor: isDark ? '#0a0a0a' : '#f8fafc',
                  borderColor: isDark ? '#ffff00' : '#f59e0b',
                  borderWidth: '2px',
                  borderStyle: 'solid',
                  boxShadow: isDark ? '0 0 15px rgba(255, 255, 0, 0.3)' : '0 0 12px rgba(245, 158, 11, 0.3)'
                }}
              >
                <TikTokIcon className="w-5 h-5" style={{ color: isDark ? '#ffff00' : '#f59e0b' }} />
              </a>
            </div>
          </div>

          {/* Products */}
          <div>
            <h4 
              className="font-semibold mb-4 transition-colors duration-300"
              style={{ 
                color: isDark ? '#ff00ff' : '#ff1493',
                textShadow: isDark ? '0 0 10px rgba(255, 0, 255, 0.6)' : '0 0 8px rgba(255, 20, 147, 0.5)'
              }}
            >
              {t('footer.signTypes')}
            </h4>
            <ul className="space-y-2">
              {productCategories.slice(0, 5).map((category) => (
                <li key={category.id}>
                  <Link 
                    to={`/products/${category.slug}`}
                    className="text-sm transition-colors duration-300 hover:underline"
                    style={{ color: isDark ? '#888' : '#64748b' }}
                  >
                    {category.name}
                  </Link>
                </li>
              ))}
              <li>
                <Link 
                  to="/products/custom-signs"
                  className="text-sm transition-colors duration-300 hover:underline"
                  style={{ color: isDark ? '#00ffff' : '#00bfff' }}
                >
                  {t('common.viewAll')} →
                </Link>
              </li>
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h4 
              className="font-semibold mb-4 transition-colors duration-300"
              style={{ 
                color: isDark ? '#00ffff' : '#0ea5e9',
                textShadow: isDark ? '0 0 10px rgba(0, 255, 255, 0.6)' : '0 0 8px rgba(14, 165, 233, 0.5)'
              }}
            >
              {t('footer.quickLinks')}
            </h4>
            <ul className="space-y-2">
              <li>
                <Link 
                  to="/services/logo-design" 
                  className="text-sm transition-colors duration-300 hover:underline"
                  style={{ color: isDark ? '#888' : '#64748b' }}
                >
                  {t('header.logoDesign')}
                </Link>
              </li>
              <li>
                <Link 
                  to="/services/business-enhancement" 
                  className="text-sm transition-colors duration-300 hover:underline"
                  style={{ color: isDark ? '#888' : '#64748b' }}
                >
                  {t('header.brandBoost')}
                </Link>
              </li>
              <li>
                <Link 
                  to="/#customizer" 
                  className="text-sm transition-colors duration-300 hover:underline"
                  style={{ color: isDark ? '#888' : '#64748b' }}
                >
                  {t('hero.ctaNeon')}
                </Link>
              </li>
              <li>
                <Link 
                  to="/#quote" 
                  className="text-sm transition-colors duration-300 hover:underline"
                  style={{ color: isDark ? '#888' : '#64748b' }}
                >
                  {t('header.getQuote')}
                </Link>
              </li>
              <li>
                <Link 
                  to="/#gallery" 
                  className="text-sm transition-colors duration-300 hover:underline"
                  style={{ color: isDark ? '#888' : '#64748b' }}
                >
                  {t('header.gallery')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 
              className="font-semibold mb-4 transition-colors duration-300"
              style={{ 
                color: isDark ? '#ffff00' : '#f59e0b',
                textShadow: isDark ? '0 0 10px rgba(255, 255, 0, 0.6)' : '0 0 8px rgba(245, 158, 11, 0.5)'
              }}
            >
              {t('footer.contact')}
            </h4>
            <ul className="space-y-3">
              <li 
                className="flex items-start gap-2 text-sm transition-colors duration-300"
                style={{ color: isDark ? '#888' : '#64748b' }}
              >
                <MapPin className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: isDark ? '#ff00ff' : '#d946ef' }} />
                <span>{companySettings.address || t('footer.address')}</span>
              </li>
              {(companySettings.phone || true) && (
                <li 
                  className="flex items-center gap-2 text-sm transition-colors duration-300"
                  style={{ color: isDark ? '#888' : '#64748b' }}
                >
                  <Phone className="w-5 h-5 flex-shrink-0" style={{ color: isDark ? '#00ffff' : '#0ea5e9' }} />
                  <a 
                    href={`tel:${companySettings.phone || '+81 98-XXX-XXXX'}`}
                    className="hover:underline transition-colors"
                  >
                    {companySettings.phone || '+81 98-XXX-XXXX'}
                  </a>
                </li>
              )}
              {(companySettings.email || true) && (
                <li 
                  className="flex items-center gap-2 text-sm transition-colors duration-300"
                  style={{ color: isDark ? '#888' : '#64748b' }}
                >
                  <Mail className="w-5 h-5 flex-shrink-0" style={{ color: isDark ? '#00ff88' : '#10b981' }} />
                  <a 
                    href={`mailto:${companySettings.email || 'info@example.com'}`}
                    className="hover:underline transition-colors"
                    style={{ color: isDark ? '#00ff88' : '#10b981' }}
                  >
                    {companySettings.email || 'info@example.com'}
                  </a>
                </li>
              )}
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div 
          className="pt-8 border-t flex flex-col md:flex-row justify-between items-center gap-4 transition-colors duration-300"
          style={{ borderColor: isDark ? 'rgba(255, 0, 255, 0.3)' : 'rgba(255, 20, 147, 0.2)' }}
        >
          <p 
            className="text-sm transition-colors duration-300"
            style={{ color: isDark ? '#666' : '#94a3b8' }}
          >
            © 2025 Okinawa Signs. {t('footer.rights')}
          </p>
          <div className="flex gap-6 text-sm">
            <Link 
              to="/commercial-disclosure" 
              className="transition-colors duration-300 hover:underline"
              style={{ color: isDark ? '#666' : '#94a3b8' }}
            >
              {t('footer.commercialDisclosure', '特定商取引法に基づく表記')}
            </Link>
            <Link 
              to="/privacy-policy" 
              className="transition-colors duration-300 hover:underline"
              style={{ color: isDark ? '#666' : '#94a3b8' }}
            >
              {t('footer.privacy')}
            </Link>
            <a 
              href="#" 
              className="transition-colors duration-300 hover:underline"
              style={{ color: isDark ? '#666' : '#94a3b8' }}
            >
              {t('footer.terms')}
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
